import { useTheme } from '@/contexts/ThemeContext';

const solutions = [
  {
    icon: 'ri-global-line',
    title: 'Sistemas Web',
    description: 'Plataformas web sob medida com arquitetura moderna, alta performance e interfaces intuitivas. Portais, intranets, sistemas de gestão e dashboards analíticos.',
    tags: ['React', 'Node.js', 'API REST'],
    accent: 'text-cyan-500',
    border: 'border-cyan-500/20',
    lightBorder: 'border-cyan-300/40',
    iconBg: 'bg-cyan-500/10',
    lightIconBg: 'bg-cyan-50',
    tagColor: 'text-cyan-600',
  },
  {
    icon: 'ri-smartphone-line',
    title: 'Aplicativos Mobile',
    description: 'Apps nativos e híbridos para iOS e Android. Desde aplicativos corporativos internos até soluções de atendimento ao cliente e automação de campo.',
    tags: ['React Native', 'iOS', 'Android'],
    accent: 'text-violet-500',
    border: 'border-violet-500/20',
    lightBorder: 'border-violet-300/40',
    iconBg: 'bg-violet-500/10',
    lightIconBg: 'bg-violet-50',
    tagColor: 'text-violet-600',
  },
  {
    icon: 'ri-graduation-cap-line',
    title: 'ERP para Escolas',
    description: 'Sistema completo de gestão escolar: matrículas, mensalidades, frequência, notas, comunicados, portal do aluno e relatórios pedagógicos integrados.',
    tags: ['Gestão Escolar', 'Portal Aluno', 'Financeiro'],
    accent: 'text-emerald-500',
    border: 'border-emerald-500/20',
    lightBorder: 'border-emerald-300/40',
    iconBg: 'bg-emerald-500/10',
    lightIconBg: 'bg-emerald-50',
    tagColor: 'text-emerald-600',
  },
  {
    icon: 'ri-recycle-line',
    title: 'ERP para Reciclagem',
    description: 'ERP especializado para empresas de reciclagem: controle de materiais, pesagem, fornecedores, estoque, logística reversa e relatórios ambientais.',
    tags: ['Controle de Materiais', 'Pesagem', 'Logística'],
    accent: 'text-lime-600',
    border: 'border-lime-500/20',
    lightBorder: 'border-lime-300/40',
    iconBg: 'bg-lime-500/10',
    lightIconBg: 'bg-lime-50',
    tagColor: 'text-lime-700',
  },
  {
    icon: 'ri-building-2-line',
    title: 'ERP Corporativo',
    description: 'ERP empresarial completo com módulos financeiros, RH, estoque, compras, vendas, CRM e BI integrado. Escalável para médias e grandes empresas.',
    tags: ['Financeiro', 'RH', 'BI Integrado'],
    accent: 'text-orange-500',
    border: 'border-orange-500/20',
    lightBorder: 'border-orange-300/40',
    iconBg: 'bg-orange-500/10',
    lightIconBg: 'bg-orange-50',
    tagColor: 'text-orange-600',
  },
  {
    icon: 'ri-link-m',
    title: 'Integrações & APIs',
    description: 'Conectamos seus sistemas legados a novas plataformas. Integrações com ERPs de mercado, gateways de pagamento, NFe, eSocial, SEFAZ e muito mais.',
    tags: ['API REST', 'Webhooks', 'NFe/SEFAZ'],
    accent: 'text-sky-500',
    border: 'border-sky-500/20',
    lightBorder: 'border-sky-300/40',
    iconBg: 'bg-sky-500/10',
    lightIconBg: 'bg-sky-50',
    tagColor: 'text-sky-600',
  },
];

export default function Solutions() {
  const { isDark } = useTheme();

  return (
    <section
      id="solucoes"
      className={`py-28 transition-colors duration-500 ${isDark ? 'bg-[#080d1a]' : 'bg-white'}`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-16 gap-4">
          <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border ${
            isDark ? 'border-cyan-500/30 bg-cyan-500/10' : 'border-cyan-400/40 bg-cyan-50'
          }`}>
            <i className="ri-apps-2-line text-cyan-500 text-sm"></i>
            <span className="text-cyan-600 text-xs font-semibold tracking-widest uppercase">Nossas Soluções</span>
          </div>
          <h2 className={`text-3xl lg:text-5xl font-bold leading-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Software especializado para{' '}
            <span className="bg-gradient-to-r from-cyan-500 to-cyan-400 bg-clip-text text-transparent">
              cada segmento
            </span>
          </h2>
          <p className={`text-lg max-w-2xl ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            Desenvolvemos soluções tecnológicas sob medida, com foco em performance, usabilidade e resultados mensuráveis para o seu negócio.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {solutions.map((sol) => (
            <div
              key={sol.title}
              className={`group relative rounded-2xl p-7 border hover:scale-[1.02] transition-all duration-300 cursor-pointer ${
                isDark
                  ? `${sol.border} bg-[#0d1526] hover:bg-[#0f1a30]`
                  : `${sol.lightBorder} bg-slate-50 hover:bg-white`
              }`}
            >
              <div className="flex flex-col gap-5">
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl border ${
                  isDark
                    ? `${sol.iconBg} border-white/10`
                    : `${sol.lightIconBg} border-slate-200`
                }`}>
                  <i className={`${sol.icon} ${sol.accent} text-2xl`}></i>
                </div>
                <div>
                  <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>{sol.title}</h3>
                  <p className={`text-sm leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{sol.description}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {sol.tags.map((tag) => (
                    <span
                      key={tag}
                      className={`text-xs px-3 py-1 rounded-full border font-medium ${sol.tagColor} ${
                        isDark ? 'border-white/10 bg-white/5' : 'border-slate-200 bg-white'
                      }`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
